import { Controller } from "@hotwired/stimulus"

// "Wer zahlt was?"-Check für die außerklinische Intensivpflege.
//
// Bewusst kein Kostenvergleich: Die Behandlungspflege trägt in allen drei
// Versorgungsformen die Krankenkasse (§ 37c SGB V), es gibt also keinen
// Pauschalbetrag zu berechnen.
//
// Darstellungsregel: Nur EINE große Zahl — der Eigenanteil für die Pflege.
// Die Leistungen der Pflegekasse fließen in die Gegenrichtung und stehen
// deshalb eine Stufe tiefer unter "Sie bekommen zusätzlich", nie gleichrangig
// neben dem Eigenanteil.
//
// Der Eigenanteil ist auf die PFLEGE bezogen, nicht aufs Wohnen: In der WG sind
// Miete und Verpflegung nicht bezifferbar (es existiert keine Erhebung), eine
// Gesamtzahl wäre dort irreführend niedrig.
//
// Rechtlich heikle Stellen:
// - Vollstationär weist § 37c Abs. 3 S. 1 SGB V der Krankenkasse auch
//   Investitionskosten sowie Unterkunft und Verpflegung zu, unter Anrechnung des
//   § 43-SGB-XI-Betrags. Es bleibt KEIN einrichtungseinheitlicher Eigenanteil —
//   hier nie vdek-Heimwerte anzeigen.
// - Pflegegeld (§ 37 Abs. 1), Entlastungsbetrag (§ 45b Abs. 1) und
//   Verhinderungspflege (§ 39 Abs. 1) setzen häusliche Pflege voraus und
//   entfallen vollstationär; dort greift allein der § 43-Betrag.
// - Die Zuzahlung (§ 37c Abs. 5, § 61 SGB V) hat ZWEI verschiedene Formeln, je
//   nach Leistungsort (§ 37c Abs. 2 S. 1): In Pflegeheim, betreuter Wohnform
//   und Intensivpflege-WG (Nr. 1-3) pauschal 10 €/Kalendertag (§ 61 S. 2). Zu
//   Hause und an vergleichbaren Orten (Nr. 4) dagegen 10 % der tatsächlichen
//   Kosten PLUS 10 € je Verordnung (§ 61 S. 3) — dort NICHT null, auch wenn
//   eine ältere Fassung dieses Reglers das fälschlich annahm. Beide Varianten
//   gelten höchstens für die ersten 28 Kalendertage im Jahr, nur ab 18 Jahren
//   und nur ohne bestehende Zuzahlungsbefreiung (§ 62 SGB V).
export default class extends Controller {
  static values = { data: Object }

  static targets = [
    "ortButton", "pflegegradButton", "angehoerigeButton", "angehoerigeBlock",
    "heroValue", "heroUnit", "heroSub",
    "geldRow", "geldLabel", "geldParagraph", "geldAmount",
    "verhinderungRow", "verhinderungAmount",
    "entlastungRow", "entlastungAmount",
    "wgZuschlagRow", "wgZuschlagAmount",
    "stationaerRow", "stationaerAmount",
    "keinAnspruchRow", "pkSumme",
    "selbstText", "selbstNote",
    "stmtKk", "stmtPk", "stmtSelbst", "stmtVerordnung"
  ]

  connect() {
    this.euro = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
    this.ort = "home"
    this.pflegegrad = 4
    this.angehoerige = true
    this.render()
  }

  selectOrt(event) {
    this.ort = event.currentTarget.dataset.value
    this.render()
  }

  selectPflegegrad(event) {
    this.pflegegrad = parseInt(event.currentTarget.dataset.value)
    this.render()
  }

  selectAngehoerige(event) {
    this.angehoerige = event.currentTarget.dataset.value === "ja"
    this.render()
  }

  // Auswahl-Optik + aria-pressed, analog zum zuschuss_rechner
  highlight(buttons, value) {
    buttons.forEach(btn => {
      const on = btn.dataset.value === String(value)
      btn.classList.toggle("border-ph24_new_blue", on)
      btn.classList.toggle("bg-ph24_new_blue/5", on)
      btn.classList.toggle("ring-2", on)
      btn.classList.toggle("ring-ph24_new_blue", on)
      btn.classList.toggle("border-gray-200", !on)
      btn.classList.toggle("hover:border-ph24_new_blue/40", !on)
      btn.setAttribute("aria-pressed", on ? "true" : "false")
    })
  }

  render() {
    const data = this.dataValue
    const pg = this.pflegegrad
    const haeuslich = this.ort !== "stationaer"
    const leistungen = data.leistungen[pg] || {}
    const zuzahlung = data.zuzahlung_tag * data.zuzahlung_max_tage

    this.highlight(this.ortButtonTargets, this.ort)
    this.highlight(this.pflegegradButtonTargets, pg)
    this.highlight(this.angehoerigeButtonTargets, this.angehoerige ? "ja" : "nein")
    this.angehoerigeBlockTarget.classList.toggle("hidden", !haeuslich)

    // --- Die eine Zahl: Eigenanteil für die Pflege ------------------------
    // WG und Einrichtung sind in § 61 S. 2 SGB V beide genannt und liegen
    // deshalb bei höchstens 280 € im Jahr. Zu Hause gilt die andere Formel aus
    // § 61 S. 3 SGB V (10 % der Kosten + 10 € je Verordnung) — kein fester
    // Eurobetrag berechenbar, deshalb Prozent statt Euro als große Zahl.
    if (this.ort === "home") {
      this.heroValueTarget.textContent = "10 %"
      this.heroUnitTarget.textContent = "der Kosten + 10 € je Verordnung"
      this.heroSubTarget.textContent = `Diese gesetzliche Zuzahlung wird höchstens für die ersten ${data.zuzahlung_max_tage} Kalendertage im Jahr erhoben, danach entfällt sie für den Rest des Jahres. Sie gilt ab 18 Jahren und nur ohne bestehende Zuzahlungsbefreiung. Wichtiger Deckel: Mehr als 2 % der jährlichen Bruttoeinnahmen (bei chronisch Kranken 1 %) zahlen Sie über alle Zuzahlungen hinweg nie — siehe Belastungsgrenze im Abschnitt Kosten.`
    } else {
      this.heroValueTarget.textContent = this.euro.format(zuzahlung)
      this.heroUnitTarget.textContent = "höchstens im Jahr"
      this.heroSubTarget.textContent = this.ort === "wg"
        ? `Mehr als die gesetzliche Zuzahlung von ${data.zuzahlung_tag} € je Kalendertag für höchstens ${data.zuzahlung_max_tage} Tage fällt für die Pflege nicht an. Miete und Verpflegung der WG kommen separat dazu.`
        : `Mehr als die gesetzliche Zuzahlung von ${data.zuzahlung_tag} € je Kalendertag für höchstens ${data.zuzahlung_max_tage} Tage fällt nicht an. Sogar Unterkunft und Verpflegung trägt hier die Krankenkasse.`
    }

    // --- Pflegekasse: Geld in die Gegenrichtung, eine Stufe tiefer --------
    const angehoerige = this.angehoerige
    const geldTyp = angehoerige ? "Pflegegeld" : "Pflegesachleistungen"
    const geld = (haeuslich && pg >= 2) ? (leistungen[geldTyp] || 0) : 0
    const verhinderung = (haeuslich && pg >= 2) ? Math.round(data.verhinderungspflege_jahr / 12) : 0
    const entlastung = (haeuslich && pg >= 1) ? (leistungen["Entlastungsbetrag"] || 0) : 0
    // § 45f Abs. 1 Nr. 2 SGB XI bindet nur die Alternative über §§ 36-38/45a an
    // Pflegegrad 2-5; wer stattdessen den Entlastungsbetrag (§ 45b) bezieht, hat
    // den Anspruch schon ab Pflegegrad 1.
    const wgZuschlag = (this.ort === "wg" && pg >= 1) ? data.wohngruppenzuschlag : 0
    const stationaer = (!haeuslich && pg >= 1) ? (leistungen["Vollstationäre Leistungen"] || 0) : 0

    this.geldRowTarget.classList.toggle("hidden", geld === 0)
    this.geldLabelTarget.textContent = geldTyp
    this.geldParagraphTarget.textContent = angehoerige ? "§ 37 SGB XI" : "§ 36 SGB XI"
    this.geldAmountTarget.textContent = this.euro.format(geld)

    this.verhinderungRowTarget.classList.toggle("hidden", verhinderung === 0)
    this.verhinderungAmountTarget.textContent = this.euro.format(verhinderung)

    this.entlastungRowTarget.classList.toggle("hidden", entlastung === 0)
    this.entlastungAmountTarget.textContent = this.euro.format(entlastung)

    this.wgZuschlagRowTarget.classList.toggle("hidden", wgZuschlag === 0)
    this.wgZuschlagAmountTarget.textContent = this.euro.format(wgZuschlag)

    this.stationaerRowTarget.classList.toggle("hidden", stationaer === 0)
    this.stationaerAmountTarget.textContent = this.euro.format(stationaer)

    const summe = geld + verhinderung + entlastung + wgZuschlag + stationaer
    this.keinAnspruchRowTarget.classList.toggle("hidden", summe > 0)
    this.pkSummeTarget.textContent = this.euro.format(summe)

    // --- Sie selbst: Wohnen, getrennt von der Pflege ----------------------
    const wohnumfeld = this.euro.format(data.wohnumfeld_zuschuss)
    if (this.ort === "home") {
      this.selbstTextTarget.textContent = "Ihre normale Miete und Lebenshaltung. Durch die Intensivpflege kommen keine Wohnkosten dazu."
      this.selbstNoteTarget.textContent = `Für einen nötigen Umbau (Pflegebett-Zugang, Rampe, Bad) zahlt die Pflegekasse einmalig bis zu ${wohnumfeld} (§ 40 SGB XI).`
    } else if (this.ort === "wg") {
      this.selbstTextTarget.textContent = "Miete, Nebenkosten, Verpflegung beziehungsweise Haushaltskasse und gegebenenfalls eine Betreuungsumlage der Wohngruppe."
      this.selbstNoteTarget.textContent = "Eine bundesweite oder nach Bundesländern aufgeschlüsselte Erhebung dieser Kosten gibt es nicht. Lassen Sie sich die Kosten der konkreten WG immer schriftlich aufschlüsseln."
    } else {
      this.selbstTextTarget.textContent = "Nichts. Unterkunft, Verpflegung und die betriebsnotwendigen Investitionskosten trägt hier die Krankenkasse. Der sonst übliche Heim-Eigenanteil entfällt."
      this.selbstNoteTarget.textContent = "Voraussetzung ist ein bestehender Anspruch nach § 37c SGB V. Entfällt er, weil sich der Gesundheitszustand bessert, zahlt die Krankenkasse noch sechs Monate weiter."
    }

    // --- Klartext ---------------------------------------------------------
    this.stmtKkTarget.textContent = this.ort === "stationaer"
      ? "Die Pflege rund um die Uhr zahlt die Krankenkasse. In der vollstationären Einrichtung auch noch zusätzlich Unterkunft, Verpflegung und Investitionskosten, unter Anrechnung des Leistungsbetrags der Pflegekasse (§ 37c Abs. 3 SGB V). Ein Mindestpflegegrad ist dafür nicht nötig."
      : "Die Pflege rund um die Uhr zahlt die Krankenkasse. Für Sie entstehen dafür keine Kosten, und ein Mindestpflegegrad ist nicht nötig. Entscheidend ist die ärztliche Verordnung (§ 37c SGB V)."

    this.stmtPkTarget.textContent = summe === 0
      ? "Ohne Pflegegrad zahlt die Pflegekasse nichts dazu. Die Intensivpflege selbst bleibt davon unberührt, den Pflegegrad-Antrag lohnt es sich also trotzdem parallel zu stellen."
      : (this.ort === "stationaer"
        ? `Von der Pflegekasse kommen ${this.euro.format(summe)} im Monat als Leistungsbetrag für die vollstationäre Pflege dazu (§ 43 SGB XI). Pflegegeld, Entlastungsbetrag und Verhinderungspflege setzen häusliche Pflege voraus und entfallen hier.`
        : `Von der Pflegekasse bekommen Sie ${this.euro.format(summe)} im Monat für Grundpflege und Alltag: ${this.euro.format(geld)} ${geldTyp}, ${this.euro.format(verhinderung)} anteilige Verhinderungspflege, ${this.euro.format(entlastung)} Entlastungsbetrag${wgZuschlag > 0 ? ` und ${this.euro.format(wgZuschlag)} Wohngruppenzuschlag` : ""}.`)

    this.stmtSelbstTarget.textContent = {
      home: `Selbst tragen Sie Ihre normale Miete sowie die gesetzliche Zuzahlung von 10 % der Kosten plus 10 € je Verordnung, begrenzt auf die ersten ${data.zuzahlung_max_tage} Tage im Jahr. Die Belastungsgrenze nach § 62 SGB V deckelt alle Zuzahlungen zusammen bei 2 % der jährlichen Bruttoeinnahmen, bei chronisch Kranken 1 %. Für einen nötigen Umbau gibt es einmalig bis zu ${wohnumfeld} (§ 40 SGB XI).`,
      wg: `Selbst tragen Sie Miete und Verpflegung der WG sowie die Zuzahlung von höchstens ${this.euro.format(zuzahlung)} im Jahr. Wie hoch die Wohnkosten ausfallen, hängt von der konkreten Wohngruppe ab, belastbare Vergleichszahlen gibt es dafür also nicht.`,
      stationaer: `Selbst tragen Sie nur die gesetzliche Zuzahlung von höchstens ${this.euro.format(zuzahlung)} im Jahr (§ 61 Satz 2 SGB V). Unter 18 Jahren entfällt sie; die Belastungsgrenze nach § 62 SGB V (2 % der jährlichen Bruttoeinnahmen, bei chronisch Kranken 1 %) kann sie weiter senken.`
    }[this.ort]

    // Statisch: hängt an der Verordnung, nicht an den Eingaben oben.
    this.stmtVerordnungTarget.textContent = "Verordnen darf nur eine besonders qualifizierte Vertragsärztin oder ein Vertragsarzt mit Genehmigung der Kassenärztlichen Vereinigung. Wird beatmet oder trachealkanüliert versorgt, kommt vor jeder Verordnung eine Potenzialerhebung dazu (mindestens alle sechs Monate). Eine Trachealkanüle allein entscheidet dabei ausdrücklich nicht über den Anspruch. Maßgeblich ist der tatsächliche Überwachungsbedarf (§ 4 Abs. 2 AKI-RL)."
  }
}
